import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import staffService from '../services/staffService';

const PermissionContext = createContext();

export function usePermission() {
  return useContext(PermissionContext);
}

export function PermissionProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [staffProfile, setStaffProfile] = useState(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      try {
        // Check if user is a brand owner
        if (userProfile?.userType === 'brand') {
          // Brand owners have all permissions
          setIsOwner(true);
          setPermissions({
            campaigns: ['campaigns_view', 'campaigns_create', 'campaigns_edit', 'campaigns_delete'],
            influencers: ['influencers_view', 'influencers_invite', 'influencers_manage'],
            content: ['content_view', 'content_approve', 'content_reject'],
            analytics: ['analytics_view', 'analytics_export'],
            payments: ['payments_view', 'payments_process'],
            reports: ['reports_view', 'reports_generate', 'reports_export'],
            messages: ['messages_view', 'messages_send']
          });
        } else {
          // Check if user is a staff member
          // In a real implementation, you would check the staff collection
          // For now, we'll assume staff login is not yet implemented
          setIsOwner(false);
          setPermissions({});
        }
      } catch (error) {
        console.error('Error fetching permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [currentUser?.uid, userProfile]);

  // Check if user has a specific permission
  const hasPermission = (permissionId) => {
    if (isOwner) return true;
    
    if (!permissionId) return false;
    
    // Split permission into module and action (e.g., 'campaigns_view' -> 'campaigns', 'view')
    const [module, action] = permissionId.split('_');
    
    return permissions[module]?.includes(permissionId) || false;
  };

  // Check if user has any permission for a module
  const hasModuleAccess = (moduleId) => {
    if (isOwner) return true;
    
    return permissions[moduleId]?.length > 0 || false;
  };

  const value = {
    permissions,
    isOwner,
    staffProfile,
    loading,
    hasPermission,
    hasModuleAccess
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}
